import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import templateApi from "../api/templateApi";
import ModulePageShell from "../components/common/ModulePageShell";
import OperationalPanel from "../components/common/OperationalPanel";
import PerformanceLocalNav from "../components/common/PerformanceLocalNav";
import StatusBadge from "../components/common/StatusBadge";
import { CompactEmptyState, InlineError, PageSkeleton } from "../components/common/PageState";
import performanceQueryKeys from "../utils/queryKeys";
import { formatDate, personName, unwrapDetail, unwrapRows } from "../utils/normalize";

const sourceLabels = {
  MANUAL: "Template items",
  CUSTOM: "Template items",
  EMPLOYEE_GOALS: "Employee goals",
  COMPETENCY_MASTER: "Competency master",
  "360_CAMPAIGN": "360 appraisal",
  STATIC_TEMPLATE_ITEMS: "Template items",
};

const sectionRules = (section = {}) => {
  const rules = [];
  if (section.isRequired || section.mandatory || section.required) rules.push("Required");
  if (section.allowEmployeeRating !== false) rules.push("Employee rating");
  if (section.allowManagerRating !== false) rules.push("Manager rating");
  if (section.commentRequired || section.commentsRequired) rules.push("Comments required");
  if (section.evidenceRequired) rules.push("Evidence required");
  return rules.length ? rules : ["No special rules"];
};

const itemTitle = (item = {}, index) =>
  item.title || item.name || item.label || item.kpiMeasure || item.itemCode || `Item ${index + 1}`;

const PerformanceTemplateDetailsPage = ({ versionMode = false }) => {
  const { templateId, version } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const sectionCardBg = useColorModeValue("white", "gray.800");

  const templateQuery = useQuery({
    queryKey: versionMode
      ? performanceQueryKeys.templates.version(templateId, version)
      : performanceQueryKeys.templates.detail(templateId),
    queryFn: () =>
      versionMode
        ? templateApi.getPerformanceTemplateVersion(templateId, version)
        : templateApi.getPerformanceTemplate(templateId),
    enabled: Boolean(templateId),
  });

  const publishMutation = useMutation({
    mutationFn: () => templateApi.publishPerformanceTemplate(templateId),
    onSuccess: () => {
      toast({ title: "Template published", status: "success", duration: 2000 });
      queryClient.invalidateQueries({ queryKey: performanceQueryKeys.templates.all });
      templateQuery.refetch();
    },
  });

  const template = unwrapDetail(templateQuery.data);
  const sections = unwrapRows(template?.sections || template?.templateSnapshot?.sections);

  return (
    <ModulePageShell
      title={versionMode ? "Template version" : "Template details"}
      description="Review appraisal format, sections, scoring and workflow configuration."
      onRefresh={() => templateQuery.refetch()}
      actions={
        <Stack direction={{ base: "column", sm: "row" }} align={{ base: "stretch", sm: "center" }} spacing={2} width={{ base: "100%", md: "auto" }}>
          <Button size="sm" variant="outline" onClick={() => navigate(`/performance/pms-format/templates/${templateId}/versions`)}>
            Versions
          </Button>
          {!versionMode ? (
            <Button size="sm" variant="outline" onClick={() => navigate(`/performance/pms-format/templates/${templateId}/edit`)}>
              Edit
            </Button>
          ) : null}
          {!versionMode ? (
            <Button size="sm" colorScheme="blue" onClick={() => publishMutation.mutate()} isLoading={publishMutation.isPending}>
              Publish
            </Button>
          ) : null}
        </Stack>
      }
    >
      <PerformanceLocalNav />
      {templateQuery.isLoading ? (
        <OperationalPanel>
          <PageSkeleton rows={5} />
        </OperationalPanel>
      ) : templateQuery.isError ? (
        <InlineError message="Unable to load template." />
      ) : !template ? (
        <CompactEmptyState title="Template not found." />
      ) : (
        <VStack align="stretch" spacing={4}>
          <OperationalPanel>
            <Flex justify="space-between" align={{ base: "stretch", sm: "flex-start" }} direction={{ base: "column", sm: "row" }} gap={3}>
              <Box minW={0}>
                <HStack wrap="wrap">
                  <Text fontSize="lg" fontWeight="semibold" wordBreak="break-word">
                    {template.name || template.title || "Untitled template"}
                  </Text>
                  <StatusBadge value={template.status || "DRAFT"} />
                </HStack>
                <Text color={mutedColor} fontSize="sm" wordBreak="break-word">
                  {template.code || "-"} · Version {version || template.version || template.currentVersion || 1}
                </Text>
              </Box>
              <Badge alignSelf={{ base: "flex-start", sm: "center" }} colorScheme={(template.totalWeightage ?? 100) === 100 ? "green" : "orange"}>
                {template.totalWeightage ?? template.weightageTotal ?? 100}% total
              </Badge>
            </Flex>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={4}>
              <Box>
                <Text fontSize="sm" color={mutedColor}>Effective from</Text>
                <Text fontWeight="semibold">{formatDate(template.effectiveFrom || template.effectiveDate)}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color={mutedColor}>Rating scale</Text>
                <Text fontWeight="semibold">{template.ratingScaleId?.name || template.ratingScale?.name || "-"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color={mutedColor}>Created by</Text>
                <Text fontWeight="semibold">{personName(template.createdBy)}</Text>
              </Box>
            </SimpleGrid>
          </OperationalPanel>

          <OperationalPanel>
            <Flex justify="space-between" align={{ base: "stretch", sm: "center" }} direction={{ base: "column", sm: "row" }} gap={2} mb={3}>
              <Box>
                <Text fontWeight="semibold">Section data</Text>
                <Text fontSize="xs" color={mutedColor}>
                  Titles, sources, rules, scoring configuration and item rows.
                </Text>
              </Box>
              <Badge alignSelf={{ base: "flex-start", sm: "center" }} colorScheme="blue">
                {sections.length} sections
              </Badge>
            </Flex>
            {sections.length === 0 ? (
              <CompactEmptyState title="No sections configured." />
            ) : (
              <VStack align="stretch" spacing={4}>
                {sections.map((section, index) => {
                  const items = (section.items || []).filter((item) => item.active !== false);
                  return (
                    <Box key={section._id || section.id || section.sectionId || `${section.code}-${index}`} bg={sectionBg} borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden">
                      <Box p={4}>
                        <Flex justify="space-between" align={{ base: "stretch", sm: "flex-start" }} direction={{ base: "column", sm: "row" }} gap={3}>
                          <Box minW={0}>
                            <Text fontWeight="semibold" fontSize="md" wordBreak="break-word">
                              Section {index + 1}: {section.title || section.name || "Untitled section"}
                            </Text>
                            <Text color={mutedColor} fontSize="xs" wordBreak="break-word">
                              {section.code || "-"} · {section.description || "No section description"}
                            </Text>
                          </Box>
                          <Badge alignSelf={{ base: "flex-start", sm: "center" }} colorScheme="green">
                            {section.weightage || 0}% weightage
                          </Badge>
                        </Flex>

                        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mt={4}>
                          <Box>
                            <Text fontSize="xs" color={mutedColor}>Type</Text>
                            <Text fontSize="sm" fontWeight="medium">{section.type || "-"}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color={mutedColor}>Source</Text>
                            <Text fontSize="sm" fontWeight="medium">{sourceLabels[section.itemSource || section.sourceType] || section.itemSource || section.sourceType || "-"}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color={mutedColor}>Calculation</Text>
                            <Text fontSize="sm" fontWeight="medium">{section.calculationMethod || "-"}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color={mutedColor}>Minimum items</Text>
                            <Text fontSize="sm" fontWeight="medium">{section.minimumItemsRequired ?? 0}</Text>
                          </Box>
                        </SimpleGrid>

                        <HStack mt={3} spacing={2} wrap="wrap">
                          {sectionRules(section).map((rule) => (
                            <Badge key={rule} variant={rule === "No special rules" ? "outline" : "subtle"}>{rule}</Badge>
                          ))}
                        </HStack>
                      </Box>

                      <Box bg={sectionCardBg} borderTopWidth="1px" borderColor={borderColor} p={4}>
                        <Flex justify="space-between" align={{ base: "stretch", sm: "center" }} direction={{ base: "column", sm: "row" }} gap={2} mb={3}>
                          <Text fontWeight="semibold" fontSize="sm">Item data</Text>
                          <Badge alignSelf={{ base: "flex-start", sm: "center" }} variant="outline">
                            {items.length} items
                          </Badge>
                        </Flex>

                        {items.length === 0 ? (
                          <Text fontSize="sm" color={mutedColor}>No static items configured for this section.</Text>
                        ) : (
                          <>
                            <VStack display={{ base: "flex", lg: "none" }} align="stretch" spacing={3}>
                              {items.map((item, itemIndex) => (
                                <Box key={item.itemId || item.itemCode || itemIndex} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                                  <Flex justify="space-between" gap={3}>
                                    <Box minW={0}>
                                      <Text fontSize="sm" fontWeight="semibold" wordBreak="break-word">{itemTitle(item, itemIndex)}</Text>
                                      <Text fontSize="xs" color={mutedColor} wordBreak="break-word">{item.itemCode || item.itemId || "-"}</Text>
                                    </Box>
                                    <Text fontSize="sm" fontWeight="semibold" flexShrink={0}>{item.weightage || 0}%</Text>
                                  </Flex>
                                  {item.kpiMeasure ? (
                                    <Text mt={2} fontSize="xs" color={mutedColor} wordBreak="break-word">KPI: {item.kpiMeasure}</Text>
                                  ) : null}
                                  {item.description ? (
                                    <Text mt={2} fontSize="xs" color={mutedColor} wordBreak="break-word">{item.description}</Text>
                                  ) : null}
                                  <HStack mt={2} spacing={2} wrap="wrap">
                                    {item.ratingRequired !== false ? <Badge variant="subtle">Rating</Badge> : null}
                                    {item.commentRequired ? <Badge variant="subtle">Comments</Badge> : null}
                                    {item.evidenceRequired ? <Badge variant="subtle">Evidence</Badge> : null}
                                  </HStack>
                                </Box>
                              ))}
                            </VStack>

                            <TableContainer display={{ base: "none", lg: "block" }}>
                              <Table size="sm">
                                <Thead>
                                  <Tr>
                                    <Th>#</Th>
                                    <Th>Item</Th>
                                    <Th>KPI / Indicator</Th>
                                    <Th isNumeric>Weightage</Th>
                                    <Th>Rules</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {items.map((item, itemIndex) => (
                                    <Tr key={item.itemId || item.itemCode || itemIndex}>
                                      <Td>{itemIndex + 1}</Td>
                                      <Td>
                                        <Text fontSize="sm" fontWeight="semibold">{itemTitle(item, itemIndex)}</Text>
                                        <Text fontSize="xs" color={mutedColor}>{item.itemCode || item.itemId || "-"}</Text>
                                      </Td>
                                      <Td maxW="520px">
                                        <Text fontSize="xs" color={mutedColor} noOfLines={3}>
                                          {item.kpiMeasure || item.description || "-"}
                                        </Text>
                                      </Td>
                                      <Td isNumeric>{item.weightage || 0}%</Td>
                                      <Td>
                                        <HStack spacing={2} wrap="wrap">
                                          {item.ratingRequired !== false ? <Badge variant="subtle">Rating</Badge> : null}
                                          {item.commentRequired ? <Badge variant="subtle">Comments</Badge> : null}
                                          {item.evidenceRequired ? <Badge variant="subtle">Evidence</Badge> : null}
                                        </HStack>
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </OperationalPanel>
        </VStack>
      )}
    </ModulePageShell>
  );
};

export default PerformanceTemplateDetailsPage;
